import { AxiosInstance } from "axios";
import { action, makeObservable, toJS } from "mobx";
import AlertMessageStore, {
  AlertMessageParams,
  AlertMessageText,
  AlertMessageType,
} from "../stores/AlertMessageStore";
import { History } from "history";
import { openLinkInNewWindow } from "../utils/navigation";
import { Language } from "../i18n/languages";
import {
  isDebugLoggingEnabled,
  logDebug,
  logError,
  logGroup,
  logGroupCollapsed,
  logGroupEnd,
} from "../utils/log";
import { ProjectController } from "../dbot/project/projectDomain";
import { MeetingType } from "../meetings/meetingsDomain";
import { TMessage } from "../i18n/tt";
import { CompanyInfo, SuggestedCompany } from "../types/CompanyInfo";
import { User } from "../types/UserInfo";
import { ObjectPermission } from "../types/Permissions";
import {
  SubscribeToWebSocketMessagesEvent,
  UnsubscribeFromWebSocketMessagesEvent,
} from "../webSocketDomain";
import { CompanyItem } from "../common/common";
import { StringFieldModel } from "../common/commonForms";
import { RootStoreData } from "./rootStoreData";
import { AlertState, ConfirmState } from "../common/alertConfirm";
import { DialogType } from "../dialogs/dialogDomain";
import { SendInvitationDialogParams } from "../stores/SendInvitationDialogStore";
import { DCRoute, DCRouteData, routes } from "../routes/routes";
import { createHref, RouteWithParams } from "../routes/router";
import { Transaction } from "../types/Transactions";
import { FileUploadConfig, FileUploadValidationError } from "../fileUpload/fileUploadDomain";
import { SelectedPortfolioOption } from "../portfolio/portfolioDomain";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type HttpResponse<T = any> = {
  data: T;
  headers?: { [key: string]: string | string[] | number | boolean | null };
};

/**
 * Using interface instead of type here because the SignedDocumentEvent
 * union type is recursive. It uses the HttpEvent with itself as the
 * generic type.
 *
 * Types are evaluated/expanded eagerly by TypeScript, whereas interfaces
 * are evaluated lazily, meaning that TypeScript can only handle recursive
 * types with interfaces.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface HttpEvent<T = any> {
  type: "http";
  method: "get" | "post" | "put" | "delete";
  url: string;
  headers?: { [key: string]: string };
  responseType?: "arraybuffer" | "blob" | "document" | "json" | "text" | "stream" | "formdata";
  data?: unknown;
  onSuccess: (res: HttpResponse<T>, state: RootStoreData) => DCEvent[];
  onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
}

export type LoadUrlEvent = { type: "load-url"; url: string; newWindow?: boolean };

export type NavigateEvent<S = unknown> = {
  type: "navigate";
  url: string;
  replace?: boolean;
  state?: S;
};

export type NavigateToEvent<R extends DCRoute = DCRoute> = {
  type: "navigate-to";
  route: RouteWithParams<DCRouteData, R>;
  replace?: boolean;
  state?: unknown;
};

export const navigateTo = <R extends DCRoute>(
  route: RouteWithParams<DCRouteData, R>,
): NavigateToEvent<R> => ({ type: "navigate-to", route });

export type ReloadEvent = { type: "reload" };

export type ShowAlertEvent = {
  type: "show-alert";
  message: AlertMessageText;
  messageType: AlertMessageType;
  messageParams?: AlertMessageParams;
};

export type UpdateStoreEvent<T = RootStoreData> = {
  type: "update-store";
  data: {
    [K in keyof T]?: T[K] | ((cur: T[K]) => T[K]);
  };
};

export const updateStore = (data: {
  [K in keyof RootStoreData]?: RootStoreData[K] | ((cur: RootStoreData[K]) => RootStoreData[K]);
}): UpdateStoreEvent => ({ type: "update-store", data });

export interface WithStoreEvent<T = RootStoreData> {
  type: "with-store";
  events: (store: T) => DCEvent[];
}

export type LogErrorEvent = { type: "log-error"; err?: unknown; msg: string; args?: unknown[] };

export type ActionNameEvent = { type: "action-name"; name: string };

/**
 * Used to describe a list of events for debugging purposes
 * @param name
 */
export const actionName = (name: string): ActionNameEvent => ({ type: "action-name", name });

export function logErrorEvent(msg: string, ...args: unknown[]): LogErrorEvent;
export function logErrorEvent(err: unknown, msg: string, ...args: unknown[]): LogErrorEvent;
export function logErrorEvent(
  errOrMsg: unknown,
  msgOrArg: unknown,
  ...args: unknown[]
): LogErrorEvent {
  if (typeof errOrMsg === "string") {
    return {
      type: "log-error",
      msg: errOrMsg,
      args: msgOrArg !== undefined || args.length ? [msgOrArg, ...args] : args,
    };
  }
  return { type: "log-error", err: errOrMsg, msg: msgOrArg as string, args };
}

export const handleLogErrorEvent = (event: LogErrorEvent, context: unknown) => {
  if (event.err) {
    logError(event.err, event.msg, { args: event.args, context });
  } else {
    logError(event.msg, { args: event.args, context });
  }
};

export const handleHttpEvent = (
  http: AxiosInstance,
  dispatch: (events: DCEvent[]) => void,
  event: HttpEvent,
  state: RootStoreData,
) => {
  http
    .request({
      method: event.method,
      url: event.url,
      headers: event.headers,
      data: event.data,
      responseType: event.responseType,
    })
    .then((res) => dispatch(event.onSuccess(res as HttpResponse, state)))
    .catch((err) => dispatch(event.onFailure(err, state)));
};

export const handleLoadUrlEvent = (event: LoadUrlEvent) => {
  if (event.newWindow) {
    openLinkInNewWindow(event.url);
  } else {
    window.location.href = event.url;
  }
};

export const handleReloadEvent = () => {
  window.location.reload();
};

export const handleShowAlertEvent = (alertStore: AlertMessageStore, event: ShowAlertEvent) => {
  alertStore.showMessage(event.messageType, event.message, event.messageParams);
};

export const handleUpdateStoreEvent = action(
  <T extends { [key: string]: unknown } = RootStoreData>(
    store: T,
    event: UpdateStoreEvent<T>,
    { enableLogging = isDebugLoggingEnabled() }: { enableLogging?: boolean } = {},
  ): T => {
    const preppedData = Object.entries(event.data).map(([k, v]) => [
      k,
      typeof v === "function" ? v(store[k]) : v,
    ]);
    preppedData.forEach(([k, v]) => {
      if (enableLogging) {
        // use makeObservable->toJS as a hack to unwrap nested proxied values, otherwise the logs often become useless
        logDebug(k, v ? toJS(makeObservable(v)) : v);
      }
      store[k as keyof T] = v;
    });
    return store;
  },
);

export const handleNavigateEvent = (history: History, event: NavigateEvent) => {
  if (event.replace) {
    history.replace(event.url, event.state);
  } else {
    history.push(event.url, event.state);
  }
};

export const handleNavigateToEvent = (history: History, event: NavigateToEvent) => {
  const href = createHref<DCRouteData, DCRoute>({ routes: routes, ...event.route });
  if (event.replace) {
    history.replace(href, event.state);
  } else {
    history.push(href, event.state);
  }
};

export const httpGet = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "get",
  ...params,
});

export const httpPost = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "post",
  ...params,
});

export const httpPut = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "put",
  ...params,
});

export const httpDelete = (params: Omit<HttpEvent, "type" | "method">): HttpEvent => ({
  type: "http",
  method: "delete",
  ...params,
});

export const navigate = (
  url: string,
  options?: { replace?: boolean; state?: unknown },
): NavigateEvent => ({
  type: "navigate",
  url,
  ...options,
});

export const loadUrl = (url: string, newWindow: boolean = true): LoadUrlEvent => ({
  type: "load-url",
  url,
  newWindow,
});

export const showAlert = (
  message: AlertMessageText,
  messageType?: AlertMessageType,
  messageParams?: AlertMessageParams,
): ShowAlertEvent => ({
  type: "show-alert",
  messageType: messageType ?? "success",
  message,
  messageParams: {
    ...messageParams,
    autoHide: messageParams?.autoHide ?? (messageType === "success" || messageType === "info"),
  },
});

export const showGeneralError = () => showAlert({ key: "general-error" }, "error");

export const showGeneralSuccess = () =>
  showAlert({ key: "general-success" }, "success", { autoHide: true });

export const showSuccess = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "success", params);

export const showWarning = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "warning", params);

export const showError = (message: AlertMessageText, params?: AlertMessageParams) =>
  showAlert(message, "error", params);

export const reload = (): ReloadEvent => ({ type: "reload" });

type ThunkEvent = { type: "thunk"; thunk: ((dispatch: EventDispatcher) => void) | undefined };

type CopyToClipboardEvent = {
  type: "copy-to-clipboard";
  text: string;
};

export type ValidateFilesEvent = {
  type: "validate-files";
  files: File[];
  fileUploadConfig: FileUploadConfig;
  onSuccess: DCEvent[];
  onFailure: (message: TMessage, error: FileUploadValidationError) => DCEvent[];
};

export type DownloadFileEvent = {
  type: "download-file";
  url: string;
  onSuccess?: DCEvent[];
  onFailure?: DCEvent[];
};

export type UpdateCompanyInfoEvent = {
  type: "update-company-info";
};

export type SetTimeoutEvent = {
  type: "set-timeout";
  timeoutId: string;
  timeoutMs: number;
  onTimeout: DCEvent[];
};

export type ClearTimeoutEvent = {
  type: "clear-timeout";
  timeoutId: string;
};

export type DCEvent =
  | { type: "closeDialog" }
  | {
      type: "editProjectName";
      name: StringFieldModel;
      save: ((name: string) => void) | ((name: string) => DCEvent[]) | DCEvent[];
      isDuplicate?: boolean;
    }
  | {
      type: "fetchTransactionLog";
      orgNo: string;
      onSuccess: (transactionLog: Transaction[]) => void;
    }
  | {
      type: "load-project";
      projectId: string;
      onSuccess: (ctrl: ProjectController) => DCEvent[];
      onFailure: (err: unknown) => DCEvent[];
    }
  | {
      type: "new-meeting";
      meetingType: MeetingType;
      orgNo: string;
      projectName?: TMessage;
      projectId?: string;
    }
  | {
      type: "preview-file";
      filename?: string;
      file: { url: string } | { data: ArrayBuffer | Blob };
      onClose?: () => void;
    }
  | { type: "searchCompanies"; query: string }
  | { type: "searchCompaniesResult"; companies: SuggestedCompany[] }
  | { type: "showAcceptCompanyTermsDialog"; companyInfo: CompanyInfo; user: User }
  | { type: "showCreatePortfolioDialog" }
  | {
      type: "showEditEmailDialog";
      externalId: string;
      email: string;
      onSuccess: (email: string) => void;
      name: string;
      customRoleId: string;
    }
  | { type: "showGeneralMeetingAnnouncementDialog" }
  | { type: "showInviteNewAdminDialog"; lang: Language; companyItems: CompanyItem[] }
  | { type: "showPaymentDialog" }
  | {
      type: "showRevokeAllDialog";
      orgNos: string[];
      externalId: string;
      name: string;
      onSuccess: () => void;
      portfolio: SelectedPortfolioOption;
    }
  | {
      type: "showSendInvitationDialog";
      data: SendInvitationDialogParams;
    }
  | { type: "update-local-storage"; key: string; val: unknown }
  | { type: "update-session-storage"; key: string; val: string }
  | { type: "show-dialog"; dialog: DialogType }
  | {
      type: "showChangePermissionDialog";
      title: string | TMessage;
      objectId: string;
      onUpdate: (permissions: ObjectPermission[]) => void;
    }
  | ActionNameEvent
  | ClearTimeoutEvent
  | CopyToClipboardEvent
  | DownloadFileEvent
  | HttpEvent
  | LoadUrlEvent
  | LogErrorEvent
  | NavigateEvent
  | NavigateToEvent
  | OrderLiveGeneralMeetingEvent
  | ReloadEvent
  | SetTimeoutEvent
  | ShowAlertEvent
  | SubscribeToWebSocketMessagesEvent
  | ThunkEvent
  | UnsubscribeFromWebSocketMessagesEvent
  | UpdateCompanyInfoEvent
  | UpdateStoreEvent
  | ValidateFilesEvent
  | WithStoreEvent;

export type OrderLiveGeneralMeetingEvent = {
  type: "order-live-general-meeting";
  orgNo: string;
  projectId?: string;
  generalMeetingDate: string;
  onSuccess: () => void;
  onFailure: () => void;
};

export type EventDispatcher = (events: DCEvent[]) => void;

export const createEventDispatcher =
  (handler: (event: DCEvent, index: number, events: DCEvent[]) => void): EventDispatcher =>
  (events: DCEvent[]) => {
    if (!events.length) {
      return;
    }
    if (events.length === 1) {
      logGroupCollapsed("dispatch", events[0].type, events[0]);
      handler(events[0], 0, events);
      logGroupEnd();
    } else {
      const name = events[0].type === "action-name" ? events[0].name : undefined;
      logGroupCollapsed("dispatch", name ?? events.map((e) => e.type));
      events.forEach((e, i, es) => {
        if (i !== 0 || !name) {
          logGroup(e.type, e);
        }
        handler(e, i, es);
        if (i !== 0 || !name) {
          logGroupEnd();
        }
      });
      logGroupEnd();
    }
  };

export type Loading = { type: "loading" };

export const loading: Loading = { type: "loading" };
export const showPaymentDialog: DCEvent = { type: "showPaymentDialog" };
export const showCreatePortfolioDialog: DCEvent = { type: "showCreatePortfolioDialog" };

export const confirmAction = (params: ConfirmState): DCEvent[] => [
  {
    type: "show-dialog",
    dialog: {
      type: "confirm",
      state: {
        ...params,
        onConfirm: [{ type: "closeDialog" }, ...params.onConfirm],
        onCancel: [{ type: "closeDialog" }, ...(params.onCancel ?? [])],
      },
    },
  },
];

export const alertAction = ({ title, text, onClose }: AlertState): DCEvent[] => [
  {
    type: "show-dialog",
    dialog: {
      type: "alert",
      state: {
        title,
        text,
        onClose: [{ type: "closeDialog" }, ...(onClose ?? [])],
      },
    },
  },
];

export const thunk = (thunk: ((dispatch: EventDispatcher) => void) | undefined): ThunkEvent => ({
  type: "thunk",
  thunk,
});

export const copyToClipboard = (text: string): CopyToClipboardEvent => ({
  type: "copy-to-clipboard",
  text,
});
